import * as React from 'react';
import { connect } from 'react-redux';

import DeliverableTypeCounters from 'modules/deliverables/components/deliverable-type-counters/DeliverableTypeCounters';
import InfluencerSelector from 'components/influencer-selector/InfluencerSelector';
import PageSection from 'components/page/page-section/PageSection';
import Pagination from 'components/pagination/Pagination';
import SortableTable from 'components/sortable-table/SortableTable';

import { __t, applyTranslations } from 'utils/language';
import { dataProvider, getCreatorColumn, getMetricsColumns, getSortedData } from '../../utils/onsite-performance';

import type {
  InfluencerCommon,
  LocationWindow,
  SortableTableColumnType,
  SortableTableSort
} from 'types';
import type { OnsiteCreatorPerformance } from '../../types/OnsitePerformance.type';

import './onsite-performance-by-creator-table.scss';

applyTranslations(require('./onsite-performance-by-creator-table.i18n.json'));

const ITEMS_PER_PAGE = 200;

const getColumns = (influencersMapById: MapByInfluencerId): Array<SortableTableColumnType> => [
  getCreatorColumn(influencersMapById),
  {
    title: __t('onsite_performance_by_creator_table.column.tasks'),
    key: 'tasks',
    width: 220,
    render: (col: OnsiteCreatorPerformance) => (
      <DeliverableTypeCounters
        deliverablesCount={col.taskCounts}
      />
    )
  },
  ...getMetricsColumns()
];

type MapByInfluencerId = {
  [x: number]: InfluencerCommon
};

type OwnProps = {
  influencersMapById: MapByInfluencerId,
  influencerProfiles: Array<InfluencerCommon>,
  performanceByCreator: Array<OnsiteCreatorPerformance>
};

type StateProps = {
  location: LocationWindow
};

type Props = OwnProps & StateProps;

const resetButton = {
  label: __t('onsite_performance_by_creator_table.buttons_all_creators'),
  icon: 'users-connected-white-bg-custom-fg'
};

const OnsitePerformanceByCreatorTable = ({
  influencersMapById,
  influencerProfiles,
  location,
  performanceByCreator
}: Props) => {
  const [page, setPage] = React.useState(1);
  const [sortingOptions, setSortingOption] = React.useState({
    sortingKey: 'gmv',
    ascending: false
  });
  const [selectedInfluencerId, setSelectedInfluencerId] = React.useState(null as number | null);

  const handleInfluencerSelect = (influencerId?: number | null) => {
    if (selectedInfluencerId !== influencerId) {
      setPage(1);
      setSelectedInfluencerId(influencerId ?? null);
    }
  };

  const renderInfluencerSelector = (): React.ReactNode => (
    <PageSection
      style="volume"
      padding="none"
      margin="none"
      border="none"
    >
      <div className="onsite-performance-by-creator-table__influencer-selector">
        <InfluencerSelector
          selectedInfluencerProfileId={selectedInfluencerId}
          influencerProfiles={influencerProfiles}
          shouldSort={false}
          resetButton={resetButton}
          onSelect={handleInfluencerSelect}
          showSearch
        />
      </div>
    </PageSection>
  );

  const filteredData = selectedInfluencerId
    ? performanceByCreator.filter(p => p.influencerId === selectedInfluencerId)
    : performanceByCreator;

  const pagination = dataProvider({
    page,
    limit: ITEMS_PER_PAGE,
    data: getSortedData(filteredData, influencersMapById, sortingOptions)
  });

  return (
    <div className="onsite-performance-by-creator-table">
      {renderInfluencerSelector()}
      <PageSection
        style="volume"
        padding="none"
        margin="none"
        border="none"
      >
        <SortableTable
          data={pagination.data}
          noScroll
          columns={getColumns(influencersMapById)}
          defaultSortingKey={sortingOptions.sortingKey}
          defaultSortingDirection={sortingOptions.ascending ? 'ascending' : 'descending'}
          layout="dense"
          customClass="onsite-performance-by-creator-table__sortable"
          onSortChanged={(updatedSortingOptions: SortableTableSort) => setSortingOption(updatedSortingOptions)}
        />
      </PageSection>
      <Pagination
        key="onsite-performance-by-creator-table-pagination"
        location={location}
        meta={pagination.meta}
        links={pagination.links}
        onClick={(updatedPage: number) => setPage(updatedPage)}
      />
    </div>
  );
};

const mapStateToProps = ({ router }): StateProps => ({
  location: router.location
});

export default connect(mapStateToProps)(OnsitePerformanceByCreatorTable);
